import type { Theme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import type { StyleClasses, WithDialogStackContext, DialogProps } from '@paintscout/ui'
import { createStyles, withStyles } from '@material-ui/core/styles'
import type { OptionsDocument } from 'paintscout'
import {
  Button,
  ConfirmationDialog,
  IconButton,
  InputField,
  Hidden,
  ClientOptionsProvider,
  withDialogStackContext,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormSection
} from '@paintscout/ui'
import { createProduct, convertProductOption } from '@paintscout/util/builder'
import type { ProductOption, Product } from '@paintscout/util/builder'
import SearchIcon from '@material-ui/icons/Search'
import React from 'react'
import ProductSelector from '../../ProductSelector'
import EditProductDialog from '../EditProductDialog'

export interface ProductSelectDialogProps extends DialogProps, WithDialogStackContext {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  onConfirm?: (productOption: Product, custom?: boolean) => void
  onCancel?: () => void
  loading?: boolean
  quoteTypes: string[]
  allowCustom?: boolean
  options?: OptionsDocument
  checkParentHasChanges?: () => boolean
}

export interface ProductSelectState {
  query?: string
}

const styles = (theme: Theme) => {
  const { pxToRem } = theme.typography
  return createStyles({
    root: {},
    tiles: {
      paddingRight: 0
    },
    dialogContent: {
      paddingTop: 0,
      [theme.breakpoints.down('sm')]: {
        padding: 0
      }
    },
    backButton: {
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: 'transparent !important',
          color: `${theme.palette.primary.main} !important`
        }
      }
    },
    contentInner: {
      padding: `0 ${theme.spacing(0)}px`,
      [theme.breakpoints.down('sm')]: {
        padding: `0 ${theme.spacing(3)}px`
      }
    },
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    formSection: {},
    searchControls: {
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
      marginBottom: '0.5em'
    },
    searchInputWrapper: {
      flexGrow: 1
    },
    customButtonWrapper: {
      flexShrink: 0,
      minWidth: pxToRem(160),
      [theme.breakpoints.down('sm')]: {
        minWidth: 'auto',
        flexShrink: 1
      }
    },
    customButton: {
      background: theme.palette.common.white,
      color: theme.paintscout.palette.primary,
      border: `1px solid ${theme.paintscout.palette.primary}`,
      whiteSpace: 'nowrap'
    }
  })
}

class ProductSelectDialog extends React.PureComponent<ProductSelectDialogProps, ProductSelectState> {
  constructor(props) {
    super(props)
    this.state = {
      query: ''
    }
  }
  public render() {
    const {
      classes,
      loading,

      options,
      quoteTypes,
      onCancel,
      allowCustom = true,
      ...otherProps
    } = this.props
    const { query } = this.state

    const leftButton = (
      <Button disabled={loading} onClick={onCancel} variant="textNoPadding" className={classes.backButton}>
        Back
      </Button>
    )

    return (
      <ClientOptionsProvider options={options}>
        <Dialog {...otherProps} className={classes.root} maxWidth={'md'} fullWidth={true} onClose={this.handleClose}>
          <DialogTitle
            loading={loading}
            rightContent={
              <IconButton size="small" onClick={this.handleClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            }
          >
            Select Product
          </DialogTitle>
          <DialogContent classes={{ root: classes.dialogContent }}>
            <FormSection classes={{ root: classes.formSection }} hideDivider={true}>
              <div className={classes.contentInner}>
                <div className={classes.searchControls}>
                  <div className={classes.searchInputWrapper}>
                    <InputField
                      id="query"
                      data-testid="product-search-input"
                      placeholder="Search products"
                      fullWidth={true}
                      value={query}
                      onChange={(ev) => this.setState({ query: ev.currentTarget.value })}
                      startAdornment={
                        <div className={classes.searchAdornment}>
                          <SearchIcon />
                        </div>
                      }
                    />
                  </div>
                  {allowCustom && (
                    <div className={classes.customButtonWrapper}>
                      <Button
                        className={classes.customButton}
                        variant={'dialog'}
                        fullWidth
                        onClick={(event: any) => {
                          this.handleChange(event, null, true)
                        }}
                      >
                        <Hidden smDown={true}>+ Custom Product</Hidden>
                        <Hidden mdUp={true}>Custom</Hidden>
                      </Button>
                    </div>
                  )}
                </div>

                <ProductSelector
                  options={options}
                  query={query}
                  quoteTypes={quoteTypes}
                  onChange={this.handleChange}
                  allowCustom={allowCustom}
                />
              </div>
            </FormSection>
          </DialogContent>
          <DialogActions leftButton={leftButton} alignLeftButtonWithTitle={true} />
        </Dialog>
      </ClientOptionsProvider>
    )
  }

  public handleClose = () => {
    const { dialogStackContext, checkParentHasChanges } = this.props
    const { query } = this.state
    const hasLocalChanges = Boolean(query)
    const hasParentChanges = checkParentHasChanges ? checkParentHasChanges() : false
    const hasChanges = hasLocalChanges || hasParentChanges

    if (hasChanges) {
      dialogStackContext.openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dialogStackContext.dismissDialog()
          dialogStackContext.dismissAllDialogs()
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else {
      dialogStackContext.dismissAllDialogs()
    }
  }

  public handleChange = (_event: any, productOption: ProductOption, custom?: boolean) => {
    if (custom) {
      const { quoteTypes, dialogStackContext } = this.props
      const item = createProduct({ quoteTypes: quoteTypes })

      dialogStackContext.openDialog(EditProductDialog, {
        item,
        isNew: true,
        isCustom: true,
        onConfirm: async (product: Product) => {
          dialogStackContext.dismissDialog()
          this.props.onConfirm(product, false)
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else if (this.props.onConfirm) {
      this.props.onConfirm(convertProductOption({ productOption }), custom)
    }
  }
}

export default withStyles(styles)(withDialogStackContext(ProductSelectDialog))
